import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Square, SkipForward, Radio, Pause, Play, Repeat } from "lucide-react";
import type { ActiveRunItem } from "@production-hub/shared";
import { formatOffset } from "@production-hub/shared";
import { cn } from "@/lib/utils";
import {
  useActiveRuns,
  useStopRun,
  useSkipNext,
  usePauseRun,
} from "@/hooks/useBridgePlayout";

interface PlayoutRunPanelProps {
  bridgeUrl: string | undefined | null;
}

/** "in 1:23" / "overdue 0:05" — the gallery reads these at a glance. */
function countdown(seconds: number): string {
  return seconds < 0
    ? `overdue ${formatOffset(-seconds)}`
    : `in ${formatOffset(seconds)}`;
}

const STATUS_LABEL: Record<ActiveRunItem["status"], string> = {
  pending: "Pending",
  on_air: "ON AIR",
  played: "Played",
  skipped: "Skipped",
  failed: "Failed",
};

function RunItemRow({
  item,
  nextLoop = false,
}: {
  item: ActiveRunItem;
  /** True for the occurrences belonging to the loop after the current one. */
  nextLoop?: boolean;
}) {
  const onAir = item.status === "on_air";
  const played = Math.max(
    0,
    item.duration_seconds - (item.remaining_seconds ?? 0),
  );

  return (
    <div
      className={cn(
        "rounded border p-2 text-sm",
        onAir && "border-destructive bg-destructive/5",
        nextLoop && "opacity-60",
      )}
      data-testid="run-item"
    >
      <div className="flex items-center gap-2">
        <Badge
          variant={
            onAir
              ? "destructive"
              : item.status === "failed"
                ? "destructive"
                : item.status === "played"
                  ? "secondary"
                  : "outline"
          }
          className={cn(onAir && "animate-pulse")}
        >
          {onAir && <Radio className="mr-1 h-3 w-3" />}
          {STATUS_LABEL[item.status]}
        </Badge>

        <span className="flex-1 truncate font-medium">{item.playout_name}</span>

        {nextLoop && (
          <span className="text-xs text-muted-foreground">next loop</span>
        )}

        {/* Pending items count down to air; the on-air item counts down to off. */}
        {item.status === "pending" && (
          <span
            className={cn(
              "font-mono text-xs",
              item.due_in_seconds < 10 && "font-semibold text-destructive",
            )}
            data-testid="run-item-countdown"
          >
            {countdown(item.due_in_seconds)}
          </span>
        )}
        {onAir && item.remaining_seconds !== undefined && (
          <span
            className="font-mono text-xs font-semibold"
            data-testid="run-item-remaining"
          >
            {formatOffset(item.remaining_seconds)} left
          </span>
        )}
        {(item.status === "played" || item.status === "skipped") && (
          <span className="text-xs text-muted-foreground">
            {item.skip_reason?.replace(/_/g, " ") ??
              new Date(item.due_at).toLocaleTimeString()}
          </span>
        )}
      </div>

      {onAir && item.duration_seconds > 0 && (
        <Progress
          value={(played / item.duration_seconds) * 100}
          className="mt-2 h-1"
        />
      )}
      {item.error && (
        <div className="mt-1 text-xs text-destructive">{item.error}</div>
      )}
    </div>
  );
}

/**
 * Live view of runs the bridge is executing. Polled once a second, which is
 * also what keeps the countdowns moving - the scheduler runs on the bridge, so
 * its clock is the one that matters, not this tab's.
 */
export function PlayoutRunPanel({ bridgeUrl }: PlayoutRunPanelProps) {
  const { data: runs = [] } = useActiveRuns(bridgeUrl);
  const stopRun = useStopRun(bridgeUrl);
  const skipNext = useSkipNext(bridgeUrl);
  const pauseRun = usePauseRun(bridgeUrl);

  if (runs.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>On air</CardTitle>
          <CardDescription>No run is active.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {runs.map((state) => {
        const onAir = state.items.find((item) => item.status === "on_air");
        const next = state.items.find((item) => item.status === "pending");

        return (
          <Card key={state.run.id} data-testid="active-run">
            <CardHeader className="flex flex-row items-start justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  {state.playlist_name}
                  {state.loop_seconds !== undefined && (
                    <Badge variant="outline" data-testid="run-loop">
                      <Repeat className="mr-1 h-3 w-3" />
                      Loop {state.cycle + 1}
                    </Badge>
                  )}
                  {state.paused && <Badge variant="outline">Paused</Badge>}
                  {onAir && (
                    <Badge variant="destructive" className="animate-pulse">
                      <Radio className="mr-1 h-3 w-3" />
                      ON AIR
                    </Badge>
                  )}
                </CardTitle>
                <CardDescription>
                  Started {new Date(state.run.anchored_at).toLocaleTimeString()}
                  {state.loop_seconds !== undefined &&
                    ` · looping every ${formatOffset(state.loop_seconds)}`}
                  {state.paused
                    ? " · paused, nothing will fire until resumed"
                    : onAir
                      ? ` · ${onAir.playout_name}, ${formatOffset(onAir.remaining_seconds ?? 0)} left`
                      : next
                        ? ` · next ${next.playout_name} ${countdown(next.due_in_seconds)}`
                        : state.loop_seconds !== undefined
                          ? " · waiting for the next loop"
                          : " · finishing"}
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    pauseRun.mutate({
                      runId: state.run.id,
                      paused: !state.paused,
                    })
                  }
                  data-testid="pause-run"
                >
                  {state.paused ? (
                    <>
                      <Play className="mr-1 h-3 w-3" />
                      Resume
                    </>
                  ) : (
                    <>
                      <Pause className="mr-1 h-3 w-3" />
                      Pause
                    </>
                  )}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => skipNext.mutate(state.run.id)}
                  disabled={!next}
                >
                  <SkipForward className="mr-1 h-3 w-3" />
                  Skip next
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => stopRun.mutate(state.run.id)}
                  data-testid="stop-run"
                >
                  <Square className="mr-1 h-3 w-3" />
                  Stop
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-1">
                {state.items.map((item) => (
                  <RunItemRow
                    key={`${item.item_id}:${item.cycle}`}
                    item={item}
                    nextLoop={item.cycle > state.cycle}
                  />
                ))}
                {state.items.length === 0 && (
                  <p className="text-sm text-muted-foreground">
                    This playlist has no items.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
